// Generador de documentos PDF A4 profesionales utilizando jsPDF y jsPDF-AutoTable
// Garantiza paginación nativa automática, cero desbordes, repetición de encabezados y descarga directa.

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatMoney, formatHours, getAlerceLogo } from '../components/ui.js';
import { CONFIG } from '../config.js';

export class PdfService {
  /**
   * Obtiene la identidad institucional y contacto por defecto o desde options.
   */
  static _resolveCompanyProfile(options = {}) {
    const p = options.companyProfile || {};
    return {
      name: p.name || CONFIG.COMPANY_NAME || 'Equipo Alerce',
      email: p.email || CONFIG.COMPANY_EMAIL || 'contacto@equipoalerce.com',
      phone: p.phone || CONFIG.COMPANY_PHONE || '+54 9 11 1234-5678',
      tagline: p.tagline || 'Diagnóstico & Mejora de Procesos con Tecnología'
    };
  }

  static _formatDateDisplay(dateStr) {
    if (!dateStr) return '';
    const parts = String(dateStr).trim().substring(0, 10).split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  }

  static _formatTimeline(startDate, endDate) {
    const s = PdfService._formatDateDisplay(startDate);
    const e = PdfService._formatDateDisplay(endDate);
    if (s && e) return `${s} al ${e}`;
    if (s) return `Desde ${s}`;
    if (e) return `Hasta ${e}`;
    return 'A convenir';
  }

  /**
   * Genera el documento PDF A4 completo según la modalidad del proyecto.
   */
  static generateProjectDocument(proj, options = {}) {
    if (!proj) throw new Error('No se proporcionó proyecto para generar el documento');

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const isTimeAndMaterials = proj.billing_mode === 'time_and_materials';
    const compProfile = this._resolveCompanyProfile(options);
    const enrichedOptions = { ...options, companyProfile: compProfile };

    if (isTimeAndMaterials) {
      this._buildTimeAndMaterialsDocument(doc, proj, enrichedOptions);
    } else {
      this._buildFixedBudgetDocument(doc, proj, enrichedOptions);
    }

    // Añadir numeración de páginas, pie institucional y cabecera de continuación en todas las hojas
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      // Mini-cabecera de continuación para páginas 2 en adelante
      if (i > 1) {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(161, 161, 170);
        doc.text(`${compProfile.name} · Diagnóstico y Mejora de Procesos con Tecnología`, 14, 10);
        const docRef = isTimeAndMaterials
          ? `Remito N° REM-${new Date().getFullYear()}-${String(proj.id).padStart(4, '0')} (Cont.)`
          : `Presupuesto N° COT-${new Date().getFullYear()}-${String(proj.id).padStart(4, '0')} (Cont.)`;
        doc.text(docRef, 196, 10, { align: 'right' });
        doc.setDrawColor(228, 228, 231);
        doc.setLineWidth(0.2);
        doc.line(14, 12, 196, 12);
      }

      doc.setDrawColor(228, 228, 231);
      doc.setLineWidth(0.2);
      doc.line(14, 285, 196, 285);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(113, 113, 122);
      doc.text(
        `Página ${i} de ${totalPages} · ${compProfile.name} · Diagnóstico y Mejora de Procesos con Tecnología · Formato Oficial A4`,
        105,
        289,
        { align: 'center' }
      );
    }

    return doc;
  }

  /**
   * Genera el conjunto de páginas HTML paginadas (Hojas A4 reales) para la previsualización interactiva.
   * Devuelve un array de strings HTML, donde cada elemento representa una hoja A4 independiente (.a4-sheet).
   */
  static buildDocumentHtmlPages(proj, options = {}) {
    if (!proj) return [];
    const compProfile = this._resolveCompanyProfile(options);
    const enrichedOptions = { ...options, companyProfile: compProfile };
    const isTimeAndMaterials = proj.billing_mode === 'time_and_materials';
    if (isTimeAndMaterials) {
      return this._buildTmHtmlPages(proj, enrichedOptions);
    } else {
      return this._buildFixedBudgetHtmlPages(proj, enrichedOptions);
    }
  }

  /**
   * Genera las hojas A4 HTML para Presupuesto Fijo (Presupuesto Comercial)
   */
  static _buildFixedBudgetHtmlPages(proj, options) {
    const {
      validityDays = Number(proj.validity_days || 15),
      docDate = new Date().toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' }),
      companyProfile = {}
    } = options;

    const compName = companyProfile.name || 'Equipo Alerce';
    const compEmail = companyProfile.email || '';
    const compPhone = companyProfile.phone || '';

    const docCode = `N° COT-${new Date().getFullYear()}-${String(proj.id).padStart(4, '0')}`;
    const currency = proj.currency || 'ARS';
    const hourlyRate = proj.rate?.hourly_rate || 0;
    const tasks = proj.tasks || [];
    const projectTimeline = PdfService._formatTimeline(proj.start_date, proj.end_date || proj.due_date);

    const renderPage1Header = () => `
      <div class="flex items-start justify-between border-b-2 border-zinc-900 pb-4">
        <div class="flex items-center gap-3">
          ${getAlerceLogo('w-10 h-10', true)}
          <div>
            <h1 class="text-base sm:text-lg font-black tracking-tight uppercase text-zinc-900">${compName} · Diagnóstico & Mejora de Procesos</h1>
            <p class="text-[11px] text-zinc-600 font-mono">Consultoría Operativa, Optimización de Procesos & Soluciones con Tecnología</p>
            ${(compEmail || compPhone) ? `
              <p class="text-[10px] text-zinc-500 font-mono mt-0.5">
                ${compEmail ? `<span>Email: ${compEmail}</span>` : ''}
                ${(compEmail && compPhone) ? ' · ' : ''}
                ${compPhone ? `<span>Tel / WhatsApp: ${compPhone}</span>` : ''}
              </p>
            ` : ''}
          </div>
        </div>
        <div class="text-right font-mono text-[11px] space-y-0.5">
          <div class="font-bold text-xs text-zinc-900">PRESUPUESTO COMERCIAL</div>
          <div class="text-zinc-600">${docCode}</div>
          <div class="text-zinc-500 text-[10px]">Fecha: ${docDate}</div>
          <div class="text-zinc-700 font-bold text-[10px]">Validez: ${validityDays} días corridos</div>
        </div>
      </div>
    `;

    const renderContinuationHeader = () => `
      <div class="flex items-center justify-between border-b border-zinc-300 pb-2 text-[11px] font-mono text-zinc-500">
        <div class="flex items-center gap-2">
          ${getAlerceLogo('w-5 h-5', true)}
          <span class="font-bold text-zinc-800">${compName} · Diagnóstico & Mejora de Procesos</span>
        </div>
        <div class="text-right">
          <span class="font-semibold text-zinc-700">${docCode} (Continuación)</span>
        </div>
      </div>
    `;

    const renderMetadataBox = () => `
      <div class="grid grid-cols-2 gap-4 border border-zinc-300 rounded p-4 text-xs avoid-break">
        <div>
          <span class="block text-[10px] uppercase font-bold tracking-wider text-zinc-500 mb-1">Cliente / Solicitante</span>
          <div class="font-bold text-sm text-zinc-900">${proj.client?.name || 'Cliente sin asignar'}</div>
          <div class="text-zinc-600 mt-0.5">Canal: ${proj.client?.channel || 'Directo'} ${proj.client?.country ? `· País: ${proj.client.country}` : ''}</div>
        </div>
        <div>
          <span class="block text-[10px] uppercase font-bold tracking-wider text-zinc-500 mb-1">Proyecto Cotizado</span>
          <div class="font-bold text-sm text-zinc-900">${proj.name}</div>
          <div class="text-zinc-600 mt-0.5">Modalidad: <b>Presupuesto Fijo (Monto Cerrado)</b></div>
          <div class="text-zinc-600">Cronograma Estimado: <b>${projectTimeline}</b></div>
          <div class="text-zinc-600">Plazo de Validez: <b>${validityDays} días corridos</b></div>
        </div>
      </div>
    `;

    const renderScope = () => `
      <div class="space-y-1 text-xs avoid-break">
        <h3 class="font-bold uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-1">
          1. Alcance y Objetivos de la Propuesta
        </h3>
        <p class="text-zinc-700 leading-relaxed">
          ${proj.description || 'Relevamiento de circuitos operativos, diagnóstico de ineficiencias, optimización de procesos e implementación de mejoras tecnológicas conforme a los requerimientos convenidos.'}
        </p>
      </div>
    `;

    const renderTasksTable = (taskList, startIndex = 0, isLastBatch = true) => `
      <div class="space-y-2">
        <h3 class="text-xs font-bold uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-1">
          2. Desglose de Entregables e Hitos de Mejora Operativa (WBS)
        </h3>
        <table class="w-full text-left text-xs border border-zinc-300">
          <thead class="bg-zinc-100 font-bold border-b border-zinc-300 text-zinc-800">
            <tr>
              <th class="p-2 w-10 text-center">N°</th>
              <th class="p-2">Entregable / Hito de Proceso</th>
              <th class="p-2 text-center w-36">Cronograma Estimado</th>
              <th class="p-2 text-center w-20">Prioridad</th>
              <th class="p-2 text-right w-28">Estimación (Horas)</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-zinc-200">
            ${taskList.length === 0 ? `
              <tr><td colspan="5" class="p-3 text-center text-zinc-500 italic">Sin entregables registrados</td></tr>
            ` : taskList.map((t, idx) => `
              <tr>
                <td class="p-2 text-center font-mono text-zinc-500">${startIndex + idx + 1}</td>
                <td class="p-2 font-medium text-zinc-900">${t.title}</td>
                <td class="p-2 text-center font-mono text-[10px] text-zinc-600 whitespace-nowrap">${PdfService._formatTimeline(t.start_date, t.due_date || t.end_date)}</td>
                <td class="p-2 text-center text-[11px] text-zinc-600">${t.priority || 'Media'}</td>
                <td class="p-2 text-right font-mono font-bold text-zinc-900">${formatHours(t.estimated_hours || 0)}</td>
              </tr>
            `).join('')}
          </tbody>
          ${isLastBatch ? `
            <tfoot class="border-t border-zinc-300 bg-zinc-50 font-mono font-bold">
              <tr>
                <td colspan="4" class="p-2 text-right font-sans uppercase text-[11px]">Total Estimado WBS:</td>
                <td class="p-2 text-right">${formatHours(proj.budget_hours || proj.total_estimated_hours || 0)}</td>
              </tr>
            </tfoot>
          ` : ''}
        </table>
      </div>
    `;

    const renderClosingBlock = () => `
      <div class="space-y-4">
        <!-- 3. Cuadro Económico -->
        <div class="space-y-2 avoid-break">
          <h3 class="text-xs font-bold uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-1">
            3. Cuadro Económico y Cotización Final
          </h3>
          <div class="border border-zinc-300 rounded overflow-hidden">
            <div class="p-4 bg-zinc-50 flex items-center justify-between border-b border-zinc-200 text-xs">
              <div class="space-y-0.5">
                <div class="text-zinc-600">Presupuesto Horario Base: <b>${proj.budget_hours || 0} horas</b> a ${formatMoney(hourlyRate, proj.rate?.currency)}/h</div>
                ${proj.discount_percent > 0 ? `
                  <div class="text-zinc-600">Bonificación / Descuento Aplicado: <b>${proj.discount_percent}%</b></div>
                ` : ''}
              </div>
              <div class="text-right">
                <span class="text-[10px] uppercase font-bold text-zinc-500 block">Monto Cerrado Presupuestado</span>
                <span class="text-xl font-black font-mono text-zinc-900">${formatMoney(proj.budget_amount, currency)}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Términos y Condiciones -->
        <div class="space-y-1 text-[11px] text-zinc-600 leading-relaxed border-t border-zinc-200 pt-2 avoid-break">
          <h4 class="font-bold uppercase tracking-wider text-zinc-800 text-[10px]">Términos y Condiciones Comerciales:</h4>
          <ul class="list-disc pl-4 space-y-0.5">
            <li><b>Monto Cerrado:</b> Esta propuesta es a presupuesto cerrado e incluye la totalidad de entregables estipulados en el WBS.</li>
            <li><b>Cronograma y Desvíos:</b> Las fechas de inicio y finalización de tareas y del proyecto corresponden al cronograma estimado y están sujetas a posibles desvíos operativos, técnicos o de dependencias ajenas al equipo. En caso de suscitarse desvíos significativos, las partes revisarán y acordarán los ajustes necesarios en los plazos de entrega y/o en los importes presupuestados.</li>
            <li><b>Desvíos de Alcance:</b> Cualquier nuevo circuito operativo, análisis o requerimiento no contemplado será cotizado como anexo independiente.</li>
            <li><b>Vigencia:</b> Los valores y condiciones expresados conservan validez por <b>${validityDays} días corridos</b> desde la fecha de emisión.</li>
          </ul>
        </div>

        <!-- Firmas -->
        <div class="pt-6 grid grid-cols-2 gap-12 text-xs avoid-break">
          <div class="border-t border-zinc-400 pt-2 text-center">
            <div class="font-bold text-zinc-900">Aceptación y Conformidad del Cliente</div>
            <div class="text-[11px] text-zinc-500">Firma / Razón Social / Fecha</div>
          </div>
          <div class="border-t border-zinc-400 pt-2 text-center">
            <div class="font-bold text-zinc-900">${compName}</div>
            <div class="text-[11px] text-zinc-500">${compName} · Diagnóstico y Mejora de Procesos con Tecnología</div>
          </div>
        </div>
      </div>
    `;

    const pages = [];
    if (tasks.length <= 8) {
      pages.push(`
        <div class="a4-sheet bg-white text-zinc-900 border border-zinc-300 shadow-xl w-full max-w-[794px] min-h-[1123px] p-8 sm:p-12 mb-6 mx-auto flex flex-col justify-between text-xs font-sans rounded relative">
          <div class="space-y-4 flex-1">
            ${renderPage1Header()}
            ${renderMetadataBox()}
            ${renderScope()}
            ${renderTasksTable(tasks, 0, true)}
            ${renderClosingBlock()}
          </div>
          <div class="pt-4 border-t border-zinc-200 flex items-center justify-between text-[10px] text-zinc-500 font-mono">
            <span>${compName} · Diagnóstico y Mejora de Procesos con Tecnología</span>
            <span>Página 1 de 1 · Formato Oficial A4</span>
          </div>
        </div>
      `);
    } else {
      const firstBatch = tasks.slice(0, 8);
      const remainingTasks = tasks.slice(8);
      const totalPages = 2;

      pages.push(`
        <div class="a4-sheet bg-white text-zinc-900 border border-zinc-300 shadow-xl w-full max-w-[794px] min-h-[1123px] p-8 sm:p-12 mb-6 mx-auto flex flex-col justify-between text-xs font-sans rounded relative">
          <div class="space-y-5 flex-1">
            ${renderPage1Header()}
            ${renderMetadataBox()}
            ${renderScope()}
            ${renderTasksTable(firstBatch, 0, false)}
          </div>
          <div class="pt-4 border-t border-zinc-200 flex items-center justify-between text-[10px] text-zinc-500 font-mono">
            <span>${compName} · Diagnóstico y Mejora de Procesos con Tecnología</span>
            <span>Página 1 de ${totalPages} · Formato Oficial A4</span>
          </div>
        </div>
      `);

      pages.push(`
        <div class="a4-sheet bg-white text-zinc-900 border border-zinc-300 shadow-xl w-full max-w-[794px] min-h-[1123px] p-8 sm:p-12 mb-6 mx-auto flex flex-col justify-between text-xs font-sans rounded relative">
          <div class="space-y-5 flex-1">
            ${renderContinuationHeader()}
            ${renderTasksTable(remainingTasks, 8, true)}
            ${renderClosingBlock()}
          </div>
          <div class="pt-4 border-t border-zinc-200 flex items-center justify-between text-[10px] text-zinc-500 font-mono">
            <span>${compName} · Diagnóstico y Mejora de Procesos con Tecnología</span>
            <span>Página 2 de ${totalPages} · Formato Oficial A4</span>
          </div>
        </div>
      `);
    }

    return pages;
  }

  /**
   * Genera las hojas A4 HTML para Horas Reales (Remito de Horas & Liquidación)
   */
  static _buildTmHtmlPages(proj, options) {
    const {
      selectedSubtaskIds = new Set(),
      dateFrom = '',
      dateTo = '',
      showSummary = true,
      showTasks = true,
      showSubtasks = true,
      docDate = new Date().toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' }),
      companyProfile = {}
    } = options;

    const compName = companyProfile.name || 'Equipo Alerce';
    const compEmail = companyProfile.email || '';
    const compPhone = companyProfile.phone || '';

    const docCode = `N° REM-${new Date().getFullYear()}-${String(proj.id).padStart(4, '0')}`;
    const allSubtasks = (proj.tasks || []).flatMap(t => (t.subtasks || []).map(st => ({
      ...st,
      taskId: t.id,
      taskTitle: t.title,
      taskPriority: t.priority,
      taskStatus: t.status
    }))).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

    const includedSubtasks = selectedSubtaskIds.size > 0
      ? allSubtasks.filter(st => selectedSubtaskIds.has(st.id))
      : allSubtasks;

    const totalActualHours = includedSubtasks.reduce((sum, st) => sum + Number(st.actual_hours || 0), 0);
    const hourlyRate = Number(proj.rate?.hourly_rate || 0);
    const rawTmAmount = totalActualHours * hourlyRate;
    const discountPercent = Number(proj.discount_percent || 0);
    const discountAmount = discountPercent > 0 ? rawTmAmount * (discountPercent / 100) : 0;
    const billableAmount = rawTmAmount - discountAmount;
    const currency = proj.rate?.currency || 'ARS';

    const periodLabel = dateFrom || dateTo
      ? `${dateFrom || 'Inicio'} al ${dateTo || 'Actualidad'}`
      : 'Horas acumuladas según selección';

    const projectTimeline = PdfService._formatTimeline(proj.start_date, proj.end_date || proj.due_date);

    const renderPage1Header = () => `
      <div class="flex items-start justify-between border-b-2 border-zinc-900 pb-4">
        <div class="flex items-center gap-3">
          ${getAlerceLogo('w-10 h-10', true)}
          <div>
            <h1 class="text-base sm:text-lg font-black tracking-tight uppercase text-zinc-900">${compName} · Diagnóstico & Mejora de Procesos</h1>
            <p class="text-[11px] text-zinc-600 font-mono">Consultoría Operativa, Optimización de Procesos & Soluciones con Tecnología</p>
            ${(compEmail || compPhone) ? `
              <p class="text-[10px] text-zinc-500 font-mono mt-0.5">
                ${compEmail ? `<span>Email: ${compEmail}</span>` : ''}
                ${(compEmail && compPhone) ? ' · ' : ''}
                ${compPhone ? `<span>Tel / WhatsApp: ${compPhone}</span>` : ''}
              </p>
            ` : ''}
          </div>
        </div>
        <div class="text-right font-mono text-[11px] space-y-0.5">
          <div class="font-bold text-xs text-zinc-900">REMITO DE HORAS & LIQUIDACIÓN</div>
          <div class="text-zinc-600">${docCode}</div>
          <div class="text-zinc-500 text-[10px]">Fecha: ${docDate}</div>
        </div>
      </div>
    `;

    const renderContinuationHeader = () => `
      <div class="flex items-center justify-between border-b border-zinc-300 pb-2 text-[11px] font-mono text-zinc-500">
        <div class="flex items-center gap-2">
          ${getAlerceLogo('w-5 h-5', true)}
          <span class="font-bold text-zinc-800">${compName} · Diagnóstico & Mejora de Procesos</span>
        </div>
        <div class="text-right">
          <span class="font-semibold text-zinc-700">${docCode} (Continuación)</span>
        </div>
      </div>
    `;

    const renderMetadataBox = () => `
      <div class="grid grid-cols-2 gap-4 border border-zinc-300 rounded p-4 text-xs avoid-break">
        <div>
          <span class="block text-[10px] uppercase font-bold tracking-wider text-zinc-500 mb-1">Cliente / Receptor</span>
          <div class="font-bold text-sm text-zinc-900">${proj.client?.name || 'Cliente sin asignar'}</div>
          <div class="text-zinc-600 mt-0.5">Canal: ${proj.client?.channel || 'Directo'} ${proj.client?.country ? `· País: ${proj.client.country}` : ''}</div>
        </div>
        <div>
          <span class="block text-[10px] uppercase font-bold tracking-wider text-zinc-500 mb-1">Proyecto & Facturación</span>
          <div class="font-bold text-sm text-zinc-900">${proj.name}</div>
          <div class="text-zinc-600 mt-0.5">Modalidad: <b>Horas Reales (Time & Materials)</b></div>
          <div class="text-zinc-600">Cronograma Proyecto: <b>${projectTimeline}</b></div>
          <div class="text-zinc-600">Período Liquidado: <b>${periodLabel}</b> (${includedSubtasks.length} subtareas)</div>
          <div class="text-zinc-600">Tarifa Pactada: <b>${proj.rate?.name || 'Local'} (${formatMoney(hourlyRate, currency)}/h)</b></div>
        </div>
      </div>
    `;

    const renderSummarySection = () => `
      <div class="space-y-2 avoid-break">
        <h3 class="text-xs font-bold uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-1">
          1. Resumen de Liquidación de Horas
        </h3>
        <table class="w-full text-left text-xs border border-zinc-300">
          <thead class="bg-zinc-100 font-bold border-b border-zinc-300 text-zinc-800">
            <tr>
              <th class="p-2.5">Concepto</th>
              <th class="p-2.5 text-center">Horas Seleccionadas</th>
              <th class="p-2.5 text-right">Tarifa / h</th>
              <th class="p-2.5 text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-zinc-200 font-mono">
            <tr>
              <td class="p-2.5 font-sans font-medium text-zinc-900">Servicios profesionales de relevamiento, diagnóstico operativo y mejora de procesos con tecnología (${proj.name})</td>
              <td class="p-2.5 text-center font-bold">${formatHours(totalActualHours)}</td>
              <td class="p-2.5 text-right">${formatMoney(hourlyRate, currency)}</td>
              <td class="p-2.5 text-right font-bold">${formatMoney(rawTmAmount, currency)}</td>
            </tr>
            ${discountPercent > 0 ? `
              <tr class="bg-zinc-50">
                <td colspan="3" class="p-2.5 text-right font-sans text-zinc-600">Bonificación / Descuento Especial (${discountPercent}%):</td>
                <td class="p-2.5 text-right font-bold text-zinc-800">-${formatMoney(discountAmount, currency)}</td>
              </tr>
            ` : ''}
          </tbody>
          <tfoot class="border-t-2 border-zinc-900 bg-zinc-100 font-bold">
            <tr>
              <td colspan="3" class="p-3 text-right font-sans uppercase text-xs tracking-wider">TOTAL A LIQUIDAR / COBRAR:</td>
              <td class="p-3 text-right text-sm font-mono text-zinc-950">${formatMoney(billableAmount, currency)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    `;

    const renderTasksSection = () => `
      <div class="space-y-2 avoid-break">
        <h3 class="text-xs font-bold uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-1">
          2. Desglose de Entregables y Circuitos de Proceso
        </h3>
        <table class="w-full text-left text-xs border border-zinc-300">
          <thead class="bg-zinc-100 font-bold border-b border-zinc-300 text-zinc-800">
            <tr>
              <th class="p-2 w-10 text-center">N°</th>
              <th class="p-2">Circuito / Tarea</th>
              <th class="p-2 text-center w-36">Cronograma Estimado</th>
              <th class="p-2 text-center">Prioridad</th>
              <th class="p-2 text-center">Estado</th>
              <th class="p-2 text-right">Horas en Período</th>
              <th class="p-2 text-right">Importe Liquidado</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-zinc-200 text-xs">
            ${(proj.tasks || []).map((t, idx) => {
              const taskIncluded = includedSubtasks.filter(st => st.taskId === t.id);
              const taskHours = taskIncluded.reduce((sum, st) => sum + Number(st.actual_hours || 0), 0);
              const taskRaw = taskHours * hourlyRate;
              const taskCost = discountPercent > 0 ? taskRaw * (1 - discountPercent / 100) : taskRaw;
              return `
                <tr>
                  <td class="p-2 text-center font-mono text-zinc-500">${idx + 1}</td>
                  <td class="p-2 font-medium text-zinc-900">${t.title}</td>
                  <td class="p-2 text-center font-mono text-[10px] text-zinc-600 whitespace-nowrap">${PdfService._formatTimeline(t.start_date, t.due_date || t.end_date)}</td>
                  <td class="p-2 text-center text-[11px] text-zinc-600">${t.priority || 'Media'}</td>
                  <td class="p-2 text-center text-[11px] text-zinc-600">${t.status || 'En curso'}</td>
                  <td class="p-2 text-right font-mono font-bold text-zinc-900">${formatHours(taskHours)}</td>
                  <td class="p-2 text-right font-mono text-zinc-900">${formatMoney(taskCost, currency)}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>
    `;

    const renderSubtasksSection = (subtaskList, isContinuation = false) => `
      <div class="space-y-2">
        <h3 class="text-xs font-bold uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-1">
          3. Bitácora de Horas e Intervenciones Operativas ${isContinuation ? '(Continuación)' : ''}
        </h3>
        <table class="w-full text-left text-[11px] border border-zinc-300">
          <thead class="bg-zinc-100 font-bold border-b border-zinc-300 text-zinc-800">
            <tr>
              <th class="p-2 w-24">Fecha</th>
              <th class="p-2">Circuito Asociado</th>
              <th class="p-2 w-20 text-center">Autor</th>
              <th class="p-2 w-16 text-right">Horas</th>
              <th class="p-2">Detalle Técnico / Mejora Operativa</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-zinc-200">
            ${subtaskList.length === 0 ? `
              <tr><td colspan="5" class="p-4 text-center text-zinc-500 italic">No hay subtareas seleccionadas para el período indicado</td></tr>
            ` : subtaskList.map(st => `
              <tr>
                <td class="p-2 font-mono text-zinc-600 whitespace-nowrap">${st.date || 'S/D'}</td>
                <td class="p-2 font-medium text-zinc-800">${st.taskTitle || 'Tarea'}</td>
                <td class="p-2 text-center text-zinc-600">${st.author || 'Martín'}</td>
                <td class="p-2 text-right font-mono font-bold text-zinc-900">${formatHours(st.actual_hours)}</td>
                <td class="p-2 text-zinc-700 leading-snug">${st.description || 'Sin detalle técnico'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;

    const renderSignaturesBlock = () => `
      <div class="space-y-4 avoid-break">
        <div class="text-[10px] text-zinc-500 italic border-l-2 border-zinc-300 pl-2 py-0.5">
          <b>Nota sobre cronograma y desvíos:</b> Las fechas proyectadas de tareas y entregables corresponden a estimaciones operativas sujetas a desvíos técnicos o contingencias ajenas al equipo. Ante desvíos significativos, las partes revisarán y coordinarán conjuntamente los plazos y/o las proyecciones de horas.
        </div>
        <div class="pt-2 grid grid-cols-2 gap-12 text-xs">
          <div class="border-t border-zinc-400 pt-2 text-center">
            <div class="font-bold text-zinc-900">Conformidad de Servicios Recibidos</div>
            <div class="text-[11px] text-zinc-500">Firma y Aclaración Cliente (${proj.client?.name || 'Cliente'})</div>
          </div>
          <div class="border-t border-zinc-400 pt-2 text-center">
            <div class="font-bold text-zinc-900">${compName}</div>
            <div class="text-[11px] text-zinc-500">${compName} · Diagnóstico y Mejora de Procesos con Tecnología</div>
          </div>
        </div>
      </div>
    `;

    // Capacidad de la primera página
    let firstPageCapacity = 6;
    if (!showSummary && !showTasks) firstPageCapacity = 14;
    else if (!showSummary || !showTasks) firstPageCapacity = 10;

    const pages = [];

    if (!showSubtasks || includedSubtasks.length <= firstPageCapacity) {
      // Todo entra en 1 sola página
      pages.push(`
        <div class="a4-sheet bg-white text-zinc-900 border border-zinc-300 shadow-xl w-full max-w-[794px] min-h-[1123px] p-8 sm:p-12 mb-6 mx-auto flex flex-col justify-between text-xs font-sans rounded relative">
          <div class="space-y-4 flex-1">
            ${renderPage1Header()}
            ${renderMetadataBox()}
            ${showSummary ? renderSummarySection() : ''}
            ${showTasks ? renderTasksSection() : ''}
            ${showSubtasks ? renderSubtasksSection(includedSubtasks, false) : ''}
            ${renderSignaturesBlock()}
          </div>
          <div class="pt-4 border-t border-zinc-200 flex items-center justify-between text-[10px] text-zinc-500 font-mono">
            <span>${compName} · Diagnóstico y Mejora de Procesos con Tecnología</span>
            <span>Página 1 de 1 · Formato Oficial A4</span>
          </div>
        </div>
      `);
    } else {
      // Paginación en 2 o más páginas
      const p1Subtasks = includedSubtasks.slice(0, firstPageCapacity);
      let remaining = includedSubtasks.slice(firstPageCapacity);
      const subtaskBatches = [];

      while (remaining.length > 0) {
        const batchSize = Math.min(remaining.length, 14);
        subtaskBatches.push(remaining.slice(0, batchSize));
        remaining = remaining.slice(batchSize);
      }

      const totalPages = 1 + subtaskBatches.length;

      // Página 1
      pages.push(`
        <div class="a4-sheet bg-white text-zinc-900 border border-zinc-300 shadow-xl w-full max-w-[794px] min-h-[1123px] p-8 sm:p-12 mb-6 mx-auto flex flex-col justify-between text-xs font-sans rounded relative">
          <div class="space-y-4 flex-1">
            ${renderPage1Header()}
            ${renderMetadataBox()}
            ${showSummary ? renderSummarySection() : ''}
            ${showTasks ? renderTasksSection() : ''}
            ${renderSubtasksSection(p1Subtasks, false)}
          </div>
          <div class="pt-4 border-t border-zinc-200 flex items-center justify-between text-[10px] text-zinc-500 font-mono">
            <span>${compName} · Diagnóstico y Mejora de Procesos con Tecnología</span>
            <span>Página 1 de ${totalPages} · Formato Oficial A4</span>
          </div>
        </div>
      `);

      // Páginas 2 en adelante
      subtaskBatches.forEach((batch, idx) => {
        const pageNumber = 2 + idx;
        const isLastPage = pageNumber === totalPages;

        pages.push(`
          <div class="a4-sheet bg-white text-zinc-900 border border-zinc-300 shadow-xl w-full max-w-[794px] min-h-[1123px] p-8 sm:p-12 mb-6 mx-auto flex flex-col justify-between text-xs font-sans rounded relative">
            <div class="space-y-4 flex-1">
              ${renderContinuationHeader()}
              ${renderSubtasksSection(batch, true)}
              ${isLastPage ? renderSignaturesBlock() : ''}
            </div>
            <div class="pt-4 border-t border-zinc-200 flex items-center justify-between text-[10px] text-zinc-500 font-mono">
              <span>${compName} · Diagnóstico y Mejora de Procesos con Tecnología</span>
              <span>Página ${pageNumber} de ${totalPages} · Formato Oficial A4</span>
            </div>
          </div>
        `);
      });
    }

    return pages;
  }

  /**
   * Construye el comprobante de Horas Reales (Remito de Horas & Liquidación de Servicios)
   */
  static _buildTimeAndMaterialsDocument(doc, proj, options) {
    const {
      selectedSubtaskIds = new Set(),
      dateFrom = '',
      dateTo = '',
      showSummary = true,
      showTasks = true,
      showSubtasks = true,
      docDate = new Date().toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' }),
      companyProfile = {}
    } = options;

    const compProfile = this._resolveCompanyProfile(options);
    const docCode = `N° REM-${new Date().getFullYear()}-${String(proj.id).padStart(4, '0')}`;

    // 1. Cabecera Institucional
    this._renderHeader(
      doc,
      'REMITO DE HORAS & LIQUIDACIÓN',
      docCode,
      docDate,
      'Resumen de Prestación Técnica de Diagnóstico y Mejora de Procesos',
      '',
      compProfile
    );

    // Filtrar subtareas incluidas
    const allSubtasks = (proj.tasks || []).flatMap(t => (t.subtasks || []).map(st => ({
      ...st,
      taskId: t.id,
      taskTitle: t.title,
      taskPriority: t.priority,
      taskStatus: t.status
    }))).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

    const includedSubtasks = selectedSubtaskIds.size > 0
      ? allSubtasks.filter(st => selectedSubtaskIds.has(st.id))
      : allSubtasks;

    const totalActualHours = includedSubtasks.reduce((sum, st) => sum + Number(st.actual_hours || 0), 0);
    const hourlyRate = Number(proj.rate?.hourly_rate || 0);
    const rawTmAmount = totalActualHours * hourlyRate;
    const discountPercent = Number(proj.discount_percent || 0);
    const discountAmount = discountPercent > 0 ? rawTmAmount * (discountPercent / 100) : 0;
    const billableAmount = rawTmAmount - discountAmount;
    const currency = proj.rate?.currency || 'ARS';

    const periodLabel = dateFrom || dateTo
      ? `${dateFrom || 'Inicio'} al ${dateTo || 'Actualidad'}`
      : 'Acumulado según subtareas seleccionadas';

    const projectTimeline = this._formatTimeline(proj.start_date, proj.end_date || proj.due_date);

    // 2. Cuadro de Metadatos de Cliente y Proyecto
    this._renderMetadataBox(doc, {
      clientTitle: 'CLIENTE / RECEPTOR:',
      clientName: proj.client?.name || 'Cliente sin asignar',
      clientMeta: `Canal: ${proj.client?.channel || 'Directo'} ${proj.client?.country ? `· País: ${proj.client.country}` : ''}`,
      projectTitle: 'PROYECTO & FACTURACIÓN:',
      projectName: proj.name,
      projectMeta1: `Modalidad: Horas Reales (Time & Materials) · Tarifa: ${proj.rate?.name || 'Local'} (${formatMoney(hourlyRate, currency)}/h)`,
      projectMeta2: `Cronograma Proyecto: ${projectTimeline} · Período: ${periodLabel}`
    });

    let currentY = 58;

    // 3. Sección Resumen Financiero
    if (showSummary) {
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(24, 24, 27);
      doc.text('1. RESUMEN DE LIQUIDACIÓN DE HORAS', 14, currentY);
      currentY += 2;

      const summaryBody = [
        [
          `Servicios profesionales de relevamiento, diagnóstico operativo y mejora de procesos con tecnología (${proj.name})`,
          formatHours(totalActualHours),
          formatMoney(hourlyRate, currency),
          formatMoney(rawTmAmount, currency)
        ]
      ];

      if (discountPercent > 0) {
        summaryBody.push([
          { content: `Bonificación / Descuento Especial (${discountPercent}%):`, colSpan: 3, styles: { halign: 'right', textColor: [82, 82, 91] } },
          `-${formatMoney(discountAmount, currency)}`
        ]);
      }

      autoTable(doc, {
        startY: currentY,
        margin: { left: 14, right: 14, top: 18, bottom: 20 },
        theme: 'plain',
        head: [['Concepto', 'Horas Seleccionadas', 'Tarifa / h', 'Subtotal']],
        body: summaryBody,
        foot: [
          [
            { content: 'TOTAL A LIQUIDAR / COBRAR:', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold', fontSize: 9 } },
            { content: formatMoney(billableAmount, currency), styles: { halign: 'right', fontStyle: 'bold', fontSize: 9.5, textColor: [9, 9, 11] } }
          ]
        ],
        headStyles: {
          fillColor: [244, 244, 245],
          textColor: [24, 24, 27],
          fontStyle: 'bold',
          fontSize: 8,
          lineColor: [212, 212, 216],
          lineWidth: 0.2
        },
        bodyStyles: {
          textColor: [39, 39, 42],
          fontSize: 8,
          lineColor: [228, 228, 231],
          lineWidth: 0.1
        },
        footStyles: {
          fillColor: [244, 244, 245],
          lineColor: [24, 24, 27],
          lineWidth: 0.4
        },
        columnStyles: {
          0: { cellWidth: 100 },
          1: { cellWidth: 28, halign: 'center', fontStyle: 'bold' },
          2: { cellWidth: 26, halign: 'right' },
          3: { cellWidth: 28, halign: 'right', fontStyle: 'bold' }
        }
      });

      currentY = doc.lastAutoTable.finalY + 8;
    }

    // 4. Sección Desglose por Tareas / Entregables WBS
    if (showTasks) {
      if (currentY > 240) {
        doc.addPage();
        currentY = 20;
      }

      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(24, 24, 27);
      doc.text('2. DESGLOSE DE ENTREGABLES Y CIRCUITOS DE PROCESO', 14, currentY);
      currentY += 2;

      const taskRows = (proj.tasks || []).map((t, idx) => {
        const taskIncluded = includedSubtasks.filter(st => st.taskId === t.id);
        const taskHours = taskIncluded.reduce((sum, st) => sum + Number(st.actual_hours || 0), 0);
        const taskRaw = taskHours * hourlyRate;
        const taskCost = discountPercent > 0 ? taskRaw * (1 - discountPercent / 100) : taskRaw;

        return [
          String(idx + 1),
          t.title,
          this._formatTimeline(t.start_date, t.due_date || t.end_date),
          t.priority || 'Media',
          t.status || 'En curso',
          formatHours(taskHours),
          formatMoney(taskCost, currency)
        ];
      });

      autoTable(doc, {
        startY: currentY,
        margin: { left: 14, right: 14, top: 18, bottom: 20 },
        showHead: 'everyPage',
        theme: 'plain',
        head: [['N°', 'Circuito / Tarea', 'Cronograma', 'Prioridad', 'Estado', 'Horas en Período', 'Importe Liquidado']],
        body: taskRows.length > 0 ? taskRows : [['-', 'Sin tareas registradas', '-', '-', '-', '0.00 hrs', '$ 0,00']],
        headStyles: {
          fillColor: [244, 244, 245],
          textColor: [24, 24, 27],
          fontStyle: 'bold',
          fontSize: 8,
          lineColor: [212, 212, 216],
          lineWidth: 0.2
        },
        bodyStyles: {
          textColor: [39, 39, 42],
          fontSize: 7.5,
          lineColor: [228, 228, 231],
          lineWidth: 0.1
        },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center', textColor: [113, 113, 122] },
          1: { cellWidth: 58, fontStyle: 'bold' },
          2: { cellWidth: 32, halign: 'center', fontSize: 7, textColor: [82, 82, 91] },
          3: { cellWidth: 18, halign: 'center' },
          4: { cellWidth: 18, halign: 'center' },
          5: { cellWidth: 22, halign: 'right', fontStyle: 'bold' },
          6: { cellWidth: 24, halign: 'right', fontStyle: 'bold' }
        }
      });

      currentY = doc.lastAutoTable.finalY + 8;
    }

    // 5. Sección Bitácora Detallada de Subtareas (Paginación automática multihito)
    if (showSubtasks) {
      if (currentY > 240) {
        doc.addPage();
        currentY = 20;
      }

      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(24, 24, 27);
      doc.text('3. BITÁCORA DE HORAS E INTERVENCIONES OPERATIVAS', 14, currentY);
      currentY += 2;

      const subtaskRows = includedSubtasks.map(st => [
        st.date || 'S/D',
        st.taskTitle || 'Tarea',
        st.author || 'Martín',
        formatHours(st.actual_hours),
        st.description || 'Sin detalle técnico'
      ]);

      autoTable(doc, {
        startY: currentY,
        margin: { left: 14, right: 14, top: 18, bottom: 20 },
        showHead: 'everyPage',
        theme: 'plain',
        head: [['Fecha', 'Circuito Asociado', 'Autor', 'Horas', 'Detalle Técnico / Mejora Operativa']],
        body: subtaskRows.length > 0 ? subtaskRows : [['-', 'No hay subtareas seleccionadas', '-', '0.00 hrs', '-']],
        headStyles: {
          fillColor: [244, 244, 245],
          textColor: [24, 24, 27],
          fontStyle: 'bold',
          fontSize: 8,
          lineColor: [212, 212, 216],
          lineWidth: 0.2
        },
        bodyStyles: {
          textColor: [39, 39, 42],
          fontSize: 7.5,
          lineColor: [228, 228, 231],
          lineWidth: 0.1
        },
        columnStyles: {
          0: { cellWidth: 22, textColor: [82, 82, 91] },
          1: { cellWidth: 38, fontStyle: 'bold' },
          2: { cellWidth: 18, halign: 'center', textColor: [82, 82, 91] },
          3: { cellWidth: 18, halign: 'right', fontStyle: 'bold' },
          4: { cellWidth: 86 }
        }
      });

      currentY = doc.lastAutoTable.finalY + 6;
    }

    // Nota sobre desvíos antes de firmas
    if (currentY > 238) {
      doc.addPage();
      currentY = 20;
    }
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(113, 113, 122);
    const noteLines = doc.splitTextToSize(
      'Nota sobre cronograma y desvíos: Las fechas proyectadas corresponden a estimaciones operativas sujetas a desvíos técnicos. Ante desvíos significativos, las partes revisarán y coordinarán conjuntamente los plazos y/o las proyecciones de horas.',
      182
    );
    doc.text(noteLines, 14, currentY);
    currentY += (noteLines.length * 3.5) + 2;

    // 6. Bloque de Firmas de Conformidad
    this._renderSignatures(doc, currentY, 'Conformidad de Servicios Recibidos', `Firma y Aclaración Cliente (${proj.client?.name || 'Cliente'})`, compProfile);
  }

  /**
   * Construye el comprobante de Presupuesto Fijo (Presupuesto Comercial de Servicios)
   */
  static _buildFixedBudgetDocument(doc, proj, options) {
    const {
      validityDays = proj.validity_days || 15,
      docDate = new Date().toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' })
    } = options;

    const compProfile = this._resolveCompanyProfile(options);
    const docCode = `N° COT-${new Date().getFullYear()}-${String(proj.id).padStart(4, '0')}`;

    // 1. Cabecera Institucional
    this._renderHeader(
      doc,
      'PRESUPUESTO COMERCIAL',
      docCode,
      docDate,
      'Propuesta Técnica de Diagnóstico Operativo y Mejora de Procesos con Tecnología',
      `Validez: ${validityDays} días corridos`,
      compProfile
    );

    const projectTimeline = this._formatTimeline(proj.start_date, proj.end_date || proj.due_date);

    // 2. Cuadro de Metadatos de Cliente y Proyecto
    this._renderMetadataBox(doc, {
      clientTitle: 'CLIENTE / SOLICITANTE:',
      clientName: proj.client?.name || 'Cliente sin asignar',
      clientMeta: `Canal: ${proj.client?.channel || 'Directo'} ${proj.client?.country ? `· País: ${proj.client.country}` : ''}`,
      projectTitle: 'PROYECTO COTIZADO:',
      projectName: proj.name,
      projectMeta1: `Modalidad: Presupuesto Fijo (Monto Cerrado) · Validez: ${validityDays} días corridos`,
      projectMeta2: `Cronograma Estimado: ${projectTimeline}`
    });

    let currentY = 58;

    // 3. Alcance y Objetivos de la Propuesta
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(24, 24, 27);
    doc.text('1. ALCANCE Y OBJETIVOS DE LA PROPUESTA', 14, currentY);
    currentY += 4;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(63, 63, 70);
    const scopeDescription = proj.description ||
      'Relevamiento de circuitos operativos, diagnóstico de ineficiencias, optimización de procesos e implementación de mejoras tecnológicas conforme a los requerimientos convenidos.';
    const splitScope = doc.splitTextToSize(scopeDescription, 182);
    doc.text(splitScope, 14, currentY);
    currentY += (splitScope.length * 4) + 6;

    // 4. Desglose WBS de Tareas
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(24, 24, 27);
    doc.text('2. DESGLOSE DE ENTREGABLES E HITOS DE MEJORA OPERATIVA (WBS)', 14, currentY);
    currentY += 2;

    const taskRows = (proj.tasks || []).map((t, idx) => [
      String(idx + 1),
      t.title,
      this._formatTimeline(t.start_date, t.due_date || t.end_date),
      t.priority || 'Media',
      formatHours(t.estimated_hours || 0)
    ]);

    const totalEstimatedHours = proj.budget_hours || proj.total_estimated_hours || 0;

    autoTable(doc, {
      startY: currentY,
      margin: { left: 14, right: 14, top: 18, bottom: 20 },
      showHead: 'everyPage',
      theme: 'plain',
      head: [['N°', 'Entregable / Hito de Proceso', 'Cronograma Estimado', 'Prioridad', 'Estimación (Horas)']],
      body: taskRows.length > 0 ? taskRows : [['-', 'Sin entregables registrados', '-', '-', '0.00 hrs']],
      foot: [
        [
          { content: 'Total Estimado WBS:', colSpan: 4, styles: { halign: 'right', fontStyle: 'bold' } },
          { content: formatHours(totalEstimatedHours), styles: { halign: 'right', fontStyle: 'bold' } }
        ]
      ],
      headStyles: {
        fillColor: [244, 244, 245],
        textColor: [24, 24, 27],
        fontStyle: 'bold',
        fontSize: 8,
        lineColor: [212, 212, 216],
        lineWidth: 0.2
      },
      bodyStyles: {
        textColor: [39, 39, 42],
        fontSize: 8,
        lineColor: [228, 228, 231],
        lineWidth: 0.1
      },
      footStyles: {
        fillColor: [244, 244, 245],
        lineColor: [24, 24, 27],
        lineWidth: 0.3
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center', textColor: [113, 113, 122] },
        1: { cellWidth: 78, fontStyle: 'bold' },
        2: { cellWidth: 42, halign: 'center', fontSize: 7, textColor: [82, 82, 91] },
        3: { cellWidth: 22, halign: 'center' },
        4: { cellWidth: 30, halign: 'right', fontStyle: 'bold' }
      }
    });

    currentY = doc.lastAutoTable.finalY + 8;

    // 5. Cuadro Económico y Cotización Final
    if (currentY > 225) {
      doc.addPage();
      currentY = 20;
    }

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(24, 24, 27);
    doc.text('3. CUADRO ECONÓMICO Y COTIZACIÓN FINAL', 14, currentY);
    currentY += 4;

    doc.setDrawColor(212, 212, 216);
    doc.setFillColor(250, 250, 250);
    doc.roundedRect(14, currentY, 182, 18, 1.5, 1.5, 'FD');

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(82, 82, 91);
    const hourlyRate = proj.rate?.hourly_rate || 0;
    const currency = proj.currency || 'ARS';
    doc.text(`Presupuesto Horario Base: ${proj.budget_hours || 0} horas a ${formatMoney(hourlyRate, proj.rate?.currency)}/h`, 18, currentY + 6);

    if (proj.discount_percent > 0) {
      doc.text(`Bonificación / Descuento Aplicado: ${proj.discount_percent}%`, 18, currentY + 12);
    }

    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(113, 113, 122);
    doc.text('MONTO CERRADO PRESUPUESTADO', 192, currentY + 6, { align: 'right' });

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(9, 9, 11);
    doc.text(formatMoney(proj.budget_amount, currency), 192, currentY + 13, { align: 'right' });

    currentY += 24;

    // 6. Términos y Condiciones
    if (currentY > 220) {
      doc.addPage();
      currentY = 20;
    }

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(39, 39, 42);
    doc.text('TÉRMINOS Y CONDICIONES COMERCIALES:', 14, currentY);
    currentY += 4;

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(82, 82, 91);
    const terms = [
      '• Monto Cerrado: Esta propuesta es a presupuesto cerrado e incluye la totalidad de entregables estipulados en el WBS.',
      '• Cronograma y Desvíos: Las fechas de inicio y finalización de tareas y del proyecto corresponden al cronograma estimado y están sujetas a posibles desvíos operativos, técnicos o de dependencias ajenas al equipo. En caso de suscitarse desvíos significativos, las partes revisarán y acordarán los ajustes necesarios en los plazos de entrega y/o en los importes presupuestados.',
      '• Desvíos de Alcance: Cualquier nuevo circuito operativo, análisis o requerimiento no contemplado será cotizado como anexo independiente.',
      `• Vigencia: Los valores y condiciones expresados conservan validez por ${validityDays} días corridos desde su emisión.`
    ];
    terms.forEach(t => {
      const splitT = doc.splitTextToSize(t, 182);
      doc.text(splitT, 14, currentY);
      currentY += (splitT.length * 3.6) + 1.2;
    });

    currentY += 4;

    // 7. Bloque de Firmas
    this._renderSignatures(doc, currentY, 'Aceptación y Conformidad del Cliente', 'Firma / Razón Social / Fecha', compProfile);
  }

  /**
   * Renderiza el encabezado corporativo con el monograma "A" de Alerce en vector
   */
  static _renderHeader(doc, title, code, date, subtitle, extraMeta = '', companyProfile = {}) {
    const compName = (companyProfile.name || 'Equipo Alerce').toUpperCase();
    const email = companyProfile.email || '';
    const phone = companyProfile.phone || '';

    // Monograma "A" Alerce
    doc.setFillColor(24, 24, 27);
    doc.roundedRect(14, 11, 10, 10, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('A', 19, 18.5, { align: 'center' });

    // Razón Social
    doc.setTextColor(24, 24, 27);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`${compName} · DIAGNÓSTICO & MEJORA DE PROCESOS`, 27, 15);

    doc.setTextColor(113, 113, 122);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Consultoría Operativa, Optimización de Procesos & Soluciones con Tecnología', 27, 19.5);

    // Si hay email o teléfono institucional, agregarlo en el encabezado
    if (email || phone) {
      const contactText = [email ? `Email: ${email}` : '', phone ? `Tel: ${phone}` : ''].filter(Boolean).join(' · ');
      doc.setFontSize(7);
      doc.setTextColor(113, 113, 122);
      doc.text(contactText, 27, 23.5);
    }

    // Bloque derecho del comprobante
    doc.setTextColor(24, 24, 27);
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 196, 14, { align: 'right' });

    doc.setTextColor(113, 113, 122);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.text(code, 196, 18, { align: 'right' });
    doc.text(`Fecha: ${date}`, 196, 22, { align: 'right' });

    if (extraMeta) {
      doc.setTextColor(82, 82, 91);
      doc.setFont('helvetica', 'bold');
      doc.text(extraMeta, 196, 26, { align: 'right' });
    }

    // Línea divisoria
    doc.setDrawColor(24, 24, 27);
    doc.setLineWidth(0.5);
    doc.line(14, 29, 196, 29);
  }

  /**
   * Renderiza el cuadro de datos del cliente y proyecto
   */
  static _renderMetadataBox(doc, meta) {
    doc.setDrawColor(212, 212, 216);
    doc.setFillColor(250, 250, 250);
    doc.roundedRect(14, 32, 182, 21, 1.5, 1.5, 'FD');

    // Columna Cliente
    doc.setFontSize(7);
    doc.setTextColor(113, 113, 122);
    doc.setFont('helvetica', 'bold');
    doc.text(meta.clientTitle, 18, 37);

    doc.setFontSize(9);
    doc.setTextColor(24, 24, 27);
    doc.text(meta.clientName, 18, 42);

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(82, 82, 91);
    doc.text(meta.clientMeta, 18, 47);

    // Columna Proyecto
    doc.setFontSize(7);
    doc.setTextColor(113, 113, 122);
    doc.setFont('helvetica', 'bold');
    doc.text(meta.projectTitle, 105, 37);

    doc.setFontSize(9);
    doc.setTextColor(24, 24, 27);
    doc.text(meta.projectName, 105, 42);

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(82, 82, 91);
    doc.text(meta.projectMeta1, 105, 46.5);
    if (meta.projectMeta2) {
      doc.text(meta.projectMeta2, 105, 50.5);
    }
  }

  /**
   * Renderiza las firmas garantizando que quepan en la página o abriendo hoja nueva
   */
  static _renderSignatures(doc, currentY, clientRole, clientNote, companyProfile = {}) {
    const compName = companyProfile.name || 'Equipo Alerce';
    let sigY = currentY + 10;
    if (sigY > 250) {
      doc.addPage();
      sigY = 30;
    }

    // Línea izquierda (Cliente)
    doc.setDrawColor(161, 161, 170);
    doc.setLineWidth(0.3);
    doc.line(20, sigY + 12, 90, sigY + 12);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(24, 24, 27);
    doc.text(clientRole, 55, sigY + 16, { align: 'center' });

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(113, 113, 122);
    doc.text(clientNote, 55, sigY + 20, { align: 'center' });

    // Línea derecha (Equipo Alerce)
    doc.line(120, sigY + 12, 190, sigY + 12);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(24, 24, 27);
    doc.text(compName, 155, sigY + 16, { align: 'center' });

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(113, 113, 122);
    doc.text(`${compName} · Diagnóstico y Mejora de Procesos con Tecnología`, 155, sigY + 20, { align: 'center' });
  }
}
